import React from "react";
import { Avatar, Box, IconButton, Stack, Typography } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloseIcon from "@mui/icons-material/Close";
import { alpha } from "@mui/material/styles";
import PulsingGreenDot from "./pulsing-green-dot";
import { AttendeeStatus } from "../attendees/utils";
import { TeamAvatar, UserAvatar } from "../avatars";
import { getContrastColor } from "../../utils/contrast-color";
import TeamChip from "./team-chip";
import { TeamSummary } from "../../types.new";


interface UserAttendeeChipProps {
  type: "user";
  userId: string;
  label: string;
  teams?: TeamSummary[];
  status?: AttendeeStatus;
}

interface InviteAttendeeChipProps {
  type: "invite";
  email: string;
}

interface TeamAttendeeChipProps {
  type: "team";
  teamId: string;
  name: string;
  color?: string;
}

export type AttendeeChipData =
  | UserAttendeeChipProps
  | InviteAttendeeChipProps
  | TeamAttendeeChipProps;

interface AttendeeChipProps {
  attendee: AttendeeChipData;
  editable?: boolean;
  onDelete?: () => void;
  fullWidth?: boolean;
  nameGap?: number | string;
}

const baseChipSx = {
  display: "flex",
  alignItems: "center",
  width: "fit-content",
  gap: 0.75,
  borderRadius: 999,
  backgroundColor: "rgba(148, 163, 184, 0.08)",
  border: "1px solid rgba(148, 163, 184, 0.25)",
  boxSizing: "border-box",
};

const statusIconSx = {
  opacity: 1,
  fontSize: "18px",
};

const getInitials = (value?: string) => {
  const source = (value || "U").trim();
  if (!source) {
    return "U";
  }

  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const getUserStatusIcon = (status?: AttendeeStatus) => {
  if (status === "inMeeting") {
    return { icon: PulsingGreenDot, color: "#1ba51f" };
  }

  if (status === "accepted") {
    return {
      icon: CheckCircleOutlineIcon,
      color: "#1ba51f",
    };
  }

  if (status === "declined") {
    return { icon: CancelOutlinedIcon, color: "#c55a53" };
  }

  if (status === "attended") {
    return { icon: CheckCircleIcon, color: "#4bb967" };
  }

  if (status === "missed") {
    return { icon: CancelIcon, color: "#c55a53" };
  }

  return {
    icon: RadioButtonUncheckedIcon,
    color: "#757575",
  };
};

const AttendeeChip: React.FC<AttendeeChipProps> = ({
  attendee,
  editable = true,
  onDelete,
  fullWidth = false,
  nameGap = 0,
}) => {
  if (attendee.type === "user") {
    const teams = attendee.teams || [];
    const initials = getInitials(attendee.label);
    const { icon: StatusIcon, color } = getUserStatusIcon(attendee.status);
    const showDelete = editable && Boolean(onDelete);
    const showStatus = Boolean(attendee.status) || !editable;

    return (
      <Box
        sx={{
          ...baseChipSx,
          width: fullWidth ? "100%" : "fit-content",
          pr: showDelete || showStatus ? 0.5 : 1,
        }}
      >
        <UserAvatar
          text={attendee.label}
          sx={{
            width: 28,
            height: 28,
            fontSize: "0.85rem",
          }}
        />

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                mr: nameGap,
                fontWeight: 600,
              }}
            >
              {attendee.label || initials}
            </Typography>

            {teams.length > 0 ? (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flexShrink: 0 }}
              >
                {teams.length <= 3 ? (
                  teams.map((team) => (
                    <TeamChip
                      key={`${attendee.userId}-${team.teamId}`}
                      teamId={team.teamId}
                      name={team.name}
                      color={team.color}
                      bold={false}
                      sx={{
                        py: 0.1, px: 0.5,
                      }}
                    />
                  ))
                ) : (
                  <Box
                    sx={{
                      px: 0.65,
                      py: 0.12,
                      borderRadius: 0.6,
                      border: "1px solid #94a3b8",
                      color: "#64748b",
                      fontSize: "0.62rem",
                      fontWeight: 600,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Part of {teams.length} Teams
                  </Box>
                )}
              </Stack>
            ) : null}
          </Stack>

          {(showStatus || showDelete) && (
            <Stack
              direction="row"
              spacing={0.25}
              alignItems="center"
              sx={{ ml: "auto", flexShrink: 0 }}
            >
              {showStatus ? (
                <StatusIcon
                  sx={{
                    ...statusIconSx,
                    color,
                  }}
                />
              ) : null}

              {showDelete ? (
                <IconButton
                  size="small"
                  title="Remove attendee"
                  onClick={onDelete}
                  sx={{
                    p: 0.25,
                    ml: showStatus ? 0.25 : 0,
                    color: "#94a3b8",
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              ) : null}
            </Stack>
          )}
        </Stack>
      </Box>
    );
  }

  if (attendee.type === "team") {
    const showDelete = editable && Boolean(onDelete);
    const color = attendee.color || "#64748b";
    const contrastColor = getContrastColor(color);

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          pl: 0,
          pr: showDelete ? 0.5 : 1,
          borderRadius: 1,
          backgroundColor: alpha(color, 0.13),
          border: "1px solid",
          borderColor: color,
          boxSizing: "border-box",
          maxWidth: fullWidth ? "100%" : "auto",
          width: fullWidth ? "100%" : "fit-content",
        }}
      >
        <TeamAvatar
          text={attendee.name}
          color={color}
          sx={{
            width: 28,
            height: 28,
            fontSize: "0.75rem",
            fontWeight: 700,
            borderRadius: "1px 0 0 1px",
            flexShrink: 0,
            ...(contrastColor ? { color: contrastColor } : {}),
          }}
        />

        <Typography
          variant="body2"
          noWrap
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 600,
            mr: nameGap,
            lineHeight: 1.2,
          }}
        >
          {attendee.name}
        </Typography>

        {showDelete ? (
          <IconButton
            size="small"
            title="Remove team"
            onClick={onDelete}
            sx={{
              p: 0.25,
              ml: 0.5,
              color: color,
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        ) : null}
      </Box>
    );
  }

  if (attendee.type === "invite") {
    const showDelete = editable && Boolean(onDelete);

    return (
      <Box
        sx={{
          ...baseChipSx,
          width: fullWidth ? "100%" : "fit-content",
          pr: showDelete ? 0.5 : 1,
        }}
      >
        <Avatar
          sx={{
            bgcolor: "#64748b",
            color: "#fff",
            width: 28,
            height: 28,
          }}
        >
          <MailOutlineIcon sx={{ fontSize: "0.75rem" }} />
        </Avatar>

        <Typography
          variant="body2"
          noWrap
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 600,
            mr: nameGap,
          }}
        >
          {attendee.email}
        </Typography>

        {showDelete ? (
          <IconButton
            size="small"
            title="Remove invite"
            onClick={onDelete}
            sx={{
              p: 0.25,
              ml: 0.5,
              color: "#94a3b8",
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        ) : null}
      </Box>
    );
  }

  return null;
};

export default AttendeeChip;
